import _ from 'lodash';
import { STATUS } from '../../../constants/constants';
import { dateFormatter } from '../../../utils/formatters';

export class PossibleMatches {
    searchInformation: any;
    accounts: any;
    overview: any;
    webMentions: any;
    filters: any;
    fallbacks: any;

    constructor() {
        this.searchInformation = null;
        this.accounts = null;
        this.filters = null;
        this.fallbacks = null;
        this.overview = null;
        this.webMentions = null;
    }

    formatFilters(possibleAccounts, possiblePersons) {
        const filters = {};
        const pipl = 'PIPL';
        possiblePersons.forEach((possiblePerson) => {
            if (!_.isEmpty(possiblePerson.advancedSearchFilters)) {
                filters[pipl] = [];
                possiblePerson.advancedSearchFilters.forEach((filter) => {
                    const endDateFilter = _.find(possiblePerson.advancedSearchFilters, { displayName: 'End date' });
                    if (filter.displayName !== 'End date') {
                        filters[pipl].push({
                            key: filter.displayName === 'Start date' ? 'Date of birth' : filter.displayName,
                            value:
                                filter.displayName === 'Start date'
                                    ? `${dateFormatter(filter.value, 'MMM D, YYYY')} - ${dateFormatter(endDateFilter.value, 'MMM D, YYYY')}`
                                    : filter.value,
                        });
                    }
                });
            }
        });
        possibleAccounts.forEach((possibleAccount) => {
            if (!_.isEmpty(possibleAccount.advancedSearchFilters)) {
                filters[possibleAccount.socialNetwork] = [];
                possibleAccount.advancedSearchFilters.forEach((filter) => {
                    const endDateFilter = _.find(possibleAccount.advancedSearchFilters, { displayName: 'End date' });
                    if (filter.displayName !== 'End date') {
                        filters[possibleAccount.socialNetwork].push({
                            key: filter.displayName === 'Start date' ? 'Date of birth' : filter.displayName,
                            value:
                                filter.displayName === 'Start date'
                                    ? `${dateFormatter(filter.value, 'MMM D, YYYY')} - ${dateFormatter(endDateFilter.value, 'MMM D, YYYY')}`
                                    : filter.value,
                        });
                    }
                });
            }
        });
        this.filters = filters;
    }

    formatFallbacks(possibleAccounts, possiblePersons) {
        const fallbacks = {};
        const pipl = 'PIPL';
        possiblePersons.forEach((possiblePerson) => {
            if (_.isBoolean(possiblePerson.isAdvancedSearchFallback)) {
                fallbacks[pipl] = possiblePerson.isAdvancedSearchFallback;
            }
        });
        possibleAccounts.forEach((possibleAccount) => {
            if (_.isBoolean(possibleAccount.isAdvancedSearchFallback)) {
                fallbacks[possibleAccount.socialNetwork] = possibleAccount.isAdvancedSearchFallback;
            }
        });
        this.fallbacks = fallbacks;
    }

    resetAccounts() {
        const updatedAccounts = {};
        _.forOwn(this.accounts, (value, key) => {
            updatedAccounts[key] = value.map((account) => {
                account.checked = false;
                return account;
            });
        });
        this.accounts = _.cloneDeep(updatedAccounts);
    }

    update(rawData) {
        if (rawData.searchInformation?.status >= 0) {
            rawData.searchInformation.status =
                rawData.searchInformation.status === 100 ? STATUS.COMPLETE : STATUS.IN_PROGRESS;
        }
        this.searchInformation = this.formatSearchInformation(rawData.searchInformation);
        if (rawData.overview) {
            rawData.overview.name =
                rawData.overview.name || (rawData.overview.names && rawData.overview.names[0]?.value);
        }
        this.overview = rawData.overview;
        this.webMentions = rawData.webMentions;
        this.updateAccounts(rawData.possibleAccounts, rawData.possiblePersons);
        this.formatFilters(rawData.possibleAccounts, rawData.possiblePersons);
        this.formatFallbacks(rawData.possibleAccounts, rawData.possiblePersons);
    }

    updateAccounts(possibleAccounts, possiblePersons) {
        if (!this.accounts) {
            this.accounts = {};
        }

        this.accounts = possiblePersons.reduce((acc, possiblePerson) => {
            const provider = possiblePerson.sources && possiblePerson.sources[0]?.provider;
            const filteredProviders = ['PIPL', 'LEXISNEXIS', 'ThatsThem'];

            if (provider && filteredProviders.includes(provider)) {
                if (!acc[provider]) {
                    acc[provider] = [];
                }

                acc[provider].push({
                    ...(possiblePerson.overview && { overview: possiblePerson.overview }),
                    ...(provider === 'ThatsThem' && { isThatsThemSource: true }),
                    matchScore: possiblePerson.matchScore,
                    image:
                        possiblePerson.overview?.profilePictures &&
                        possiblePerson.overview?.profilePictures[0]?.value?.imgLocalSrc,
                    name: possiblePerson.overview?.names && possiblePerson.overview?.names[0]?.value,
                    userName:
                        possiblePerson.overview?.usernames && possiblePerson.overview?.usernames[0]?.value?.userName,
                    id: possiblePerson.searchPointer,
                    added: possiblePerson.searchInput?.isPersonOfInterest,
                    checked: _.find(this.accounts?.[provider], { id: possiblePerson.searchPointer })?.checked,
                });
            }
            return acc;
        }, {});

        possibleAccounts.forEach((possibleAccount) => {
            this.accounts[possibleAccount.socialNetwork] = possibleAccount.possibleAccounts.map((account) => {
                const sources = [{ source: possibleAccount.socialNetwork, provider: possibleAccount.socialNetwork }];
                account.description = account.profileDescription || account.additionalData;
                account.location = account.profileLocation || account.location;
                account.url = account.url || account.profileUrl;
                account.overview = {
                    names: [{ value: account.name, sources: sources }],
                    usernames: [{ value: account.userName, sources: sources }],
                };
                if (account.location) {
                    account.overview.locations = [{ value: { address: account.location }, sources: sources }];
                }
                if (account.workplace) {
                    account.overview.work = [{ value: { jobTitle: account.workplace }, sources: sources }];
                }

                if (account.searchInput?.isPersonOfInterest) {
                    account.added = account.searchInput?.isPersonOfInterest;
                }
                delete account.searchInput;
                account.checked = _.find(this.accounts[possibleAccount.socialNetwork], {
                    id: account.id,
                })?.checked;
                if (account.searchInput?.isPersonOfInterest) {
                    account.isThatsThemSource = account.isThatsThemSource;
                }
                return account;
            });
        });
    }

    updateCheckedAccount(updatedAccount, checked) {
        const updatedAccounts = {};
        _.forOwn(this.accounts, (value, key) => {
            updatedAccounts[key] = value.map((account) => {
                if (updatedAccount.id === account.id) {
                    account.checked = checked;
                }
                return account;
            });
        });
        this.accounts = _.cloneDeep(updatedAccounts);
    }

    updateAddedAccounts(accountIds) {
        const updatedAccounts = {};
        _.forOwn(this.accounts, (value, key) => {
            updatedAccounts[key] = value.map((account) => {
                account.checked = false;
                if (accountIds.includes(account.id)) {
                    account.added = true;
                }
                return account;
            });
        });
        this.accounts = _.cloneDeep(updatedAccounts);
    }

    formatSearchInformation(searchInformation) {
        searchInformation.advancedSearchInputs = searchInformation.advancedSearchInputs
            .map((advancedSearchInput) => {
                advancedSearchInput.key = advancedSearchInput.displayName;
                if (advancedSearchInput.displayName === 'Start date') {
                    const endDateInput = _.find(searchInformation.advancedSearchInputs, { displayName: 'End date' });
                    advancedSearchInput.key = 'Date of birth';
                    advancedSearchInput.value = `${dateFormatter(advancedSearchInput.value, 'MMM D, YYYY')} - ${dateFormatter(endDateInput.value, 'MMM D, YYYY')}`;
                }
                return advancedSearchInput;
            })
            .filter((advancedSearchInput) => advancedSearchInput.key !== 'End date');
        return searchInformation;
    }
}
